import { createReducer } from '@reduxjs/toolkit';
import {
  APPLY_CHAT_REALTIME_MESSAGE,
  CLEAR_CHAT_THREAD,
  CREATE_CHAT_DIRECT,
  GET_CHAT_CONVERSATIONS,
  GET_CHAT_MESSAGES,
  GET_CHAT_UNREAD,
  LOG_OUT,
  MARK_CHAT_CONVERSATION_READ,
  SELECT_CHAT_CONVERSATION,
  SEND_CHAT_MESSAGE,
  SET_CHAT_HAS_UNREAD,
  SET_LOGIN_FALSE
} from '../constants';
import { requestFail, requestPending, requestSuccess } from '../../utils/status';
import {
  appendUniqueMessage,
  applyRealtimeToInbox,
  hasAnyUnread,
  listHasMoreFromPage,
  LIST_MERGE,
  mergeConversationDisplay,
  mergeConversationList,
  mergeFetchedMessages,
  messagesHasMoreFromPage,
  parseConversation,
  parseConversations,
  parseMessages,
  resolveListMergeMode,
  sameConversationId,
  toChronological,
  toInboxRow
} from '../../views/chat/chatReducerHelpers';

const initialState = {
  conversations: [],
  page: 1,
  listStatus: '',
  listError: null,
  listHasMore: true,
  listLoadingMore: false,
  listRequestId: 0,
  listScope: { building: 0, org: 0, q: '' },
  unreadRequestId: 0,
  selectedConversation: null,
  messages: [],
  messagesStatus: '',
  messagesError: null,
  messagesHasMore: true,
  messagesRequestId: 0,
  sendStatus: '',
  sendConversationId: 0,
  markReadStatus: '',
  markReadConversationId: 0,
  createDirectStatus: '',
  createDirectError: null,
  hasUnread: false
};

const resetThreadFields = {
  selectedConversation: null,
  messages: [],
  messagesStatus: '',
  messagesError: null,
  messagesHasMore: true,
  sendStatus: '',
  sendConversationId: 0,
  markReadStatus: '',
  markReadConversationId: 0,
  createDirectStatus: '',
  createDirectError: null
};

export default createReducer(initialState, {
  [SELECT_CHAT_CONVERSATION]: (state, { payload }) => ({
    ...state,
    selectedConversation: payload || null,
    messages: [],
    messagesStatus: '',
    messagesError: null,
    messagesHasMore: true
  }),

  [CLEAR_CHAT_THREAD]: (state) => ({
    ...state,
    ...resetThreadFields
  }),

  [SET_CHAT_HAS_UNREAD]: (state, { payload }) => ({
    ...state,
    hasUnread: Boolean(payload)
  }),

  [requestSuccess(GET_CHAT_UNREAD)]: (state, { payload }) => {
    const requestMeta = payload?._request || {};
    const requestId = Number(requestMeta.requestId) || 0;
    if (requestId && state.unreadRequestId && requestId !== state.unreadRequestId) {
      return state;
    }
    return {
      ...state,
      hasUnread: Boolean(payload?.has_unread)
    };
  },

  [requestPending(GET_CHAT_UNREAD)]: (state, { payload }) => ({
    ...state,
    unreadRequestId: Number(payload?.requestId) || state.unreadRequestId
  }),

  [APPLY_CHAT_REALTIME_MESSAGE]: (state, { payload }) => {
    const result = applyRealtimeToInbox({
      conversations: state.conversations,
      selectedConversation: state.selectedConversation,
      messages: state.messages,
      message: payload?.message,
      conversation: payload?.conversation,
      currentUserId: Number(payload?.currentUserId) || 0
    });
    if (!result.changed) return state;
    // Do not derive hasUnread from the loaded inbox page; /chat/unread +
    // setChatHasUnread own the header badge (pagination / other buildings).
    return {
      ...state,
      conversations: result.conversations,
      selectedConversation: result.selectedConversation,
      messages: result.messages
    };
  },

  [requestPending(GET_CHAT_CONVERSATIONS)]: (state, { payload }) => {
    const page = Number(payload?.page) || 1;
    const mode = resolveListMergeMode({ page, replace: payload?.replace });
    const isReplace = mode === LIST_MERGE.REPLACE;
    const requestId = Number(payload?.requestId) || state.listRequestId;
    return {
      ...state,
      listStatus: requestPending(GET_CHAT_CONVERSATIONS),
      listError: null,
      listRequestId: requestId,
      listLoadingMore: !isReplace && (page > 1 || state.conversations.length > 0),
      // Do not clear rows here. ChatPanel already shows a loader while replace is
      // pending. Clearing + a superseded/cancelled fetch was leaving SUCCESS/empty
      // (or empty under loader) even when Network showed the building's conversation.
      ...(isReplace
        ? {
            page: 1,
            listHasMore: true
          }
        : {})
    };
  },

  [requestSuccess(GET_CHAT_CONVERSATIONS)]: (state, { payload }) => {
    const requestMeta = payload?._request || {};
    const requestId = Number(requestMeta.requestId) || 0;
    if (requestId && state.listRequestId && requestId !== state.listRequestId) {
      return state;
    }

    const incoming = parseConversations(payload).map(toInboxRow).filter(Boolean);
    const page = Number(requestMeta.page || payload?.page || 1) || 1;
    const mode = resolveListMergeMode({ page, replace: requestMeta.replace });
    const conversations = mergeConversationList(state.conversations, incoming, mode);

    let selectedConversation = state.selectedConversation;
    if (selectedConversation?.id) {
      const fresh = conversations.find((c) =>
        sameConversationId(c.id, selectedConversation.id)
      );
      if (fresh) {
        selectedConversation = mergeConversationDisplay(fresh, selectedConversation);
      }
    }

    return {
      ...state,
      conversations,
      selectedConversation,
      page,
      listHasMore: listHasMoreFromPage(incoming),
      listLoadingMore: false,
      listStatus: requestSuccess(GET_CHAT_CONVERSATIONS),
      listError: null,
      listScope: {
        building: Number(requestMeta.building) || 0,
        org: Number(requestMeta.org) || 0,
        q: requestMeta.q || ''
      },
      // Badge comes from /chat/unread (and realtime setChatHasUnread). Never reset
      // it from a partial inbox page on REPLACE; APPEND may only turn it on.
      hasUnread:
        mode === LIST_MERGE.REPLACE
          ? state.hasUnread
          : hasAnyUnread(conversations)
            ? true
            : state.hasUnread
    };
  },

  [requestFail(GET_CHAT_CONVERSATIONS)]: (state, { payload }) => {
    const requestMeta = payload?._request || {};
    const requestId = Number(requestMeta.requestId) || 0;
    if (requestId && state.listRequestId && requestId !== state.listRequestId) {
      return state;
    }
    return {
      ...state,
      listStatus: requestFail(GET_CHAT_CONVERSATIONS),
      listError: payload?.data?.message || payload?.message || 'Failed to load conversations',
      listLoadingMore: false
      // Keep conversations so a failed/cancelled request does not blank a valid inbox.
    };
  },

  [requestPending(GET_CHAT_MESSAGES)]: (state, { payload }) => ({
    ...state,
    messagesStatus: requestPending(GET_CHAT_MESSAGES),
    messagesError: null,
    messagesRequestId: Number(payload?.requestId) || state.messagesRequestId
  }),

  [requestSuccess(GET_CHAT_MESSAGES)]: (state, { payload }) => {
    const requestMeta = payload?._request || {};
    const requestId = Number(requestMeta.requestId) || 0;
    if (requestId && state.messagesRequestId && requestId !== state.messagesRequestId) {
      return state;
    }

    const requestConversationId = Number(requestMeta.conversationId) || 0;
    const selectedId = Number(state.selectedConversation?.id) || 0;
    // Ignore stale responses after switching or closing threads.
    if (!selectedId || (requestConversationId && requestConversationId !== selectedId)) {
      return state;
    }

    const fetched = toChronological(parseMessages(payload));
    const beforeId = Number(requestMeta.beforeId) || 0;
    // Latest page replaces; older pages merge/prepend.
    const messages =
      beforeId > 0 ? mergeFetchedMessages(fetched, state.messages) : fetched;

    return {
      ...state,
      messages,
      messagesStatus: requestSuccess(GET_CHAT_MESSAGES),
      messagesError: null,
      messagesHasMore: messagesHasMoreFromPage(fetched)
    };
  },

  [requestFail(GET_CHAT_MESSAGES)]: (state, { payload }) => {
    const requestMeta = payload?._request || {};
    const requestId = Number(requestMeta.requestId) || 0;
    if (requestId && state.messagesRequestId && requestId !== state.messagesRequestId) {
      return state;
    }
    const requestConversationId = Number(requestMeta.conversationId) || 0;
    const selectedId = Number(state.selectedConversation?.id) || 0;
    if (!selectedId || (requestConversationId && requestConversationId !== selectedId)) {
      return state;
    }
    return {
      ...state,
      messagesStatus: requestFail(GET_CHAT_MESSAGES),
      messagesError: payload?.data?.message || payload?.message || 'Failed to load messages'
    };
  },

  [requestPending(MARK_CHAT_CONVERSATION_READ)]: (state, { payload }) => ({
    ...state,
    markReadStatus: requestPending(MARK_CHAT_CONVERSATION_READ),
    markReadConversationId: Number(payload?.conversationId) || 0
  }),

  [requestSuccess(MARK_CHAT_CONVERSATION_READ)]: (state, { payload }) => {
    const markedId =
      Number(payload?._request?.conversationId) || Number(state.markReadConversationId) || 0;
    const clearPending =
      !state.markReadConversationId ||
      !markedId ||
      Number(state.markReadConversationId) === markedId;
    if (!markedId) {
      return {
        ...state,
        ...(clearPending
          ? {
              markReadStatus: requestSuccess(MARK_CHAT_CONVERSATION_READ),
              markReadConversationId: 0
            }
          : {})
      };
    }
    const conversations = state.conversations.map((c) =>
      sameConversationId(c.id, markedId) ? { ...c, unread_count: 0 } : c
    );
    const selectedMatches = sameConversationId(state.selectedConversation?.id, markedId);
    return {
      ...state,
      ...(clearPending
        ? {
            markReadStatus: requestSuccess(MARK_CHAT_CONVERSATION_READ),
            markReadConversationId: 0
          }
        : {}),
      conversations,
      selectedConversation:
        selectedMatches && state.selectedConversation
          ? { ...state.selectedConversation, unread_count: 0 }
          : state.selectedConversation
      // Do not derive hasUnread from the loaded inbox page only (pagination / other
      // buildings). Saga refetches /chat/unread after mark-read for the badge.
    };
  },

  [requestFail(MARK_CHAT_CONVERSATION_READ)]: (state, { payload }) => {
    const failedId =
      Number(payload?._request?.conversationId) || Number(state.markReadConversationId) || 0;
    const clearPending =
      !state.markReadConversationId ||
      !failedId ||
      Number(state.markReadConversationId) === failedId;
    if (!clearPending) return state;
    return {
      ...state,
      markReadStatus: requestFail(MARK_CHAT_CONVERSATION_READ),
      markReadConversationId: 0
    };
  },

  [requestPending(SEND_CHAT_MESSAGE)]: (state, { payload }) => ({
    ...state,
    sendStatus: requestPending(SEND_CHAT_MESSAGE),
    sendConversationId: Number(payload?.conversationId) || 0
  }),

  [requestSuccess(SEND_CHAT_MESSAGE)]: (state, { payload }) => {
    const message = payload?.id ? payload : null;
    const requestConversationId =
      Number(payload?._request?.conversationId) ||
      Number(message?.conversation_id) ||
      Number(state.sendConversationId) ||
      0;
    const clearPending =
      !state.sendConversationId ||
      !requestConversationId ||
      Number(state.sendConversationId) === requestConversationId;
    const statusPatch = clearPending
      ? {
          sendStatus: requestSuccess(SEND_CHAT_MESSAGE),
          sendConversationId: 0
        }
      : {};

    if (!message?.id) {
      return { ...state, ...statusPatch };
    }
    // Do not append into the open thread if the user already switched away.
    const selectedId = Number(state.selectedConversation?.id) || 0;
    if (requestConversationId && selectedId && requestConversationId !== selectedId) {
      return {
        ...state,
        ...statusPatch,
        conversations: state.conversations.map((c) =>
          sameConversationId(c.id, requestConversationId)
            ? {
                ...c,
                last_message_at: message.created_at,
                last_message_preview: message.body
              }
            : c
        )
      };
    }
    return {
      ...state,
      ...statusPatch,
      messages: appendUniqueMessage(state.messages, message),
      conversations: state.conversations.map((c) =>
        sameConversationId(c.id, requestConversationId || selectedId)
          ? {
              ...c,
              last_message_at: message.created_at,
              last_message_preview: message.body,
              unread_count: 0
            }
          : c
      )
    };
  },

  [requestFail(SEND_CHAT_MESSAGE)]: (state, { payload }) => {
    const failedId =
      Number(payload?._request?.conversationId) || Number(state.sendConversationId) || 0;
    const clearPending =
      !state.sendConversationId ||
      !failedId ||
      Number(state.sendConversationId) === failedId;
    if (!clearPending) return state;
    return {
      ...state,
      sendStatus: requestFail(SEND_CHAT_MESSAGE),
      sendConversationId: 0
    };
  },

  [requestPending(CREATE_CHAT_DIRECT)]: (state) => ({
    ...state,
    createDirectStatus: requestPending(CREATE_CHAT_DIRECT),
    createDirectError: null
  }),

  [requestSuccess(CREATE_CHAT_DIRECT)]: (state, { payload }) => {
    const row = toInboxRow(parseConversation(payload));
    if (!row?.id) {
      return { ...state, createDirectStatus: requestSuccess(CREATE_CHAT_DIRECT) };
    }
    const existing = state.conversations.find((c) => sameConversationId(c.id, row.id));
    const merged = mergeConversationDisplay(row, existing);
    const conversations = existing
      ? state.conversations.map((c) =>
          sameConversationId(c.id, row.id)
            ? { ...merged, unread_count: c.unread_count || 0 }
            : c
        )
      : [merged, ...state.conversations];
    return {
      ...state,
      createDirectStatus: requestSuccess(CREATE_CHAT_DIRECT),
      createDirectError: null,
      conversations,
      selectedConversation: merged,
      messages: [],
      messagesStatus: '',
      messagesError: null,
      messagesHasMore: true
    };
  },

  [requestFail(CREATE_CHAT_DIRECT)]: (state, { payload }) => ({
    ...state,
    createDirectStatus: requestFail(CREATE_CHAT_DIRECT),
    createDirectError: payload?.data?.message || payload?.message || 'Failed to start chat'
  }),

  [requestSuccess(LOG_OUT)]: () => ({ ...initialState }),
  [SET_LOGIN_FALSE]: () => ({ ...initialState })
});
